import React from "react";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  useSortable,
  rectSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import DragIndicatorIcon from "@material-ui/icons/DragIndicator";
import DocumentView from "../../DocumentView";
import { ObservationPhotoEditorImages } from "../../ImageEditor";
import ObservationInfo from "../../ObservationInfo";
import ObservationPageTitle from "../../ObservationPageTitle";
import TopRightTitle from "../../TopRightTitle";
import { ObservationsEditable as OET } from "../ReportObservations";

export default function ObservationPhotos({
  index,
  photos,
  style,
  roomIndex,
  title,
  onPhotosReorder,
}) {
  const sensors = useSensors(useSensor(PointerSensor));

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (active.id !== over?.id && onPhotosReorder) {
      const oldIndex = photos.findIndex((p) => p.id === active.id);
      const newIndex = photos.findIndex((p) => p.id === over?.id);
      const newPhotos = arrayMove(photos, oldIndex, newIndex);
      onPhotosReorder(newPhotos, roomIndex, index);
    }
  };

  return (
    <DocumentView style={style}>
      <div className="document-head" style={{ border: "none" }}>
        <header>
          <div className="grid-pg1">
            <div>
              <ObservationPageTitle />
            </div>
            <div className="grid-pg1-item2">
              <TopRightTitle />
            </div>
          </div>
        </header>
      </div>

      <ObservationInfo index={index} roomIndex={roomIndex} />

      <div className="doc-body">
        <div>
          <div className="comments-heading">
            <p>
              <strong>Photos</strong>
            </p>
          </div>

          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={photos.map((p) => p.id)}
              strategy={rectSortingStrategy}
            >
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
                  gap: "1rem",
                  padding: "10px 0",
                }}
              >
                {photos.map((photo, i) => (
                  <SortablePhotoCard
                    key={photo.id}
                    id={photo.id}
                    photo={photo}
                    index={i}
                    oi={index}
                    ri={roomIndex}
                    title={title}
                    photos={photos}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        </div>
      </div>
    </DocumentView>
  );
}

function SortablePhotoCard({ id, photo, index, oi, ri, title, photos }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    background: "#fff",
    border: "1px solid #ddd",
    borderRadius: "8px",
    padding: "10px",
    boxShadow: isDragging ? "0 4px 20px rgba(0,0,0,0.2)" : "none",
    position: "relative",
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes}>
      {/* Drag Handle Icon */}
      <div
        {...listeners}
        style={{
          position: "absolute",
          top: "50%",
          left: "1px",
          transform: "translateY(-50%)",
          width: "28px",
          height: "28px",
          background: "rgb(240 240 240 / 42%)",
          //border: "1px solid #ccc",
          borderRadius: "4px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: "grab",
          zIndex: 10,
          fontSize: "18px",
          color: "#74b44a",
        }}
        title="Drag to reorder"
      >
        <DragIndicatorIcon />
      </div>

      <ObservationPhotoEditorImages
        src={photo.url}
        rotation={photo.rotation ?? 0}
        alt=""
        className="obs-photo-img"
        photos={photos}
        oi={oi}
        pi={index}
        ri={ri}
        title={title}
      />
      <div className="obs-photo-caption">
        <OET reduxKey="caption" ml="200" oi={oi} pi={index} ri={ri}>
          {photo.caption}
        </OET>
      </div>
    </div>
  );
}
